'use client';

import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';

type AppType = 'admin' | 'client';
type RouteType = 'login' | 'register' | 'dashboard' | 'home';

interface AppNavigationContextType {
	navigateToApp: (app: AppType, route: RouteType) => void;
	navigateToClientLogin: () => void;
	navigateToClientRegister: () => void;
	navigateToClientDashboard: () => void;
	navigateToClientHome: () => void;
	navigateToAdminLogin: () => void;
	navigateToAdminDashboard: () => void;
	navigateToAdminHome: () => void;
	createAppLink: (app: AppType, route: RouteType) => string;
}

interface NavigationConfig {
	getAppUrl: (app: AppType, route: RouteType) => string;
}

const AppNavigationContext = createContext<AppNavigationContextType | undefined>(undefined);

export const AppNavigationProvider = ({
	children,
	config,
}: {
	children: ReactNode;
	config: NavigationConfig;
}) => {
	const createAppLink = useCallback(
		(app: AppType, route: RouteType): string => config.getAppUrl(app, route),
		[config]
	);

	const navigateToApp = useCallback(
		(app: AppType, route: RouteType) => {
			const url = config.getAppUrl(app, route);
			globalThis.location.assign(url);
		},
		[config]
	);

	const value = useMemo(
		() => ({
			navigateToApp,
			navigateToClientLogin: () => navigateToApp('client', 'login'),
			navigateToClientRegister: () => navigateToApp('client', 'register'),
			navigateToClientDashboard: () => navigateToApp('client', 'dashboard'),
			navigateToClientHome: () => navigateToApp('client', 'home'),
			navigateToAdminLogin: () => navigateToApp('admin', 'login'),
			navigateToAdminDashboard: () => navigateToApp('admin', 'dashboard'),
			navigateToAdminHome: () => navigateToApp('admin', 'home'),
			createAppLink,
		}),
		[navigateToApp, createAppLink]
	);

	return <AppNavigationContext.Provider value={value}>{children}</AppNavigationContext.Provider>;
};

export const useAppNavigation = () => {
	const context = useContext(AppNavigationContext);
	if (!context) throw new Error('useAppNavigation must be used within AppNavigationProvider');
	return context;
};
